import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import {
  IconChevronDown,
  IconChevronUp,
  cssSpacing,
  cssColorValue,
} from '@spotify-internal/encore-web';

const selectedStyles = css`
  border-color: transparent;
  background: ${cssColorValue('essentialBase')};
  color: ${cssColorValue('backgroundBase')};

  &:hover {
    border-color: transparent;
  }
`;

const StyledButton = styled.button<{ $hasSelection: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${cssSpacing('tighter')};
  padding: ${cssSpacing('tighter-4')} ${cssSpacing('base')};
  min-height: 32px;
  border: 1px solid ${cssColorValue('essentialSubdued')};
  border-radius: 100px;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  color: ${cssColorValue('textBase')};
  white-space: nowrap;

  &:hover {
    border-color: ${cssColorValue('essentialBase')};
  }

  ${({ $hasSelection }) => $hasSelection && selectedStyles}
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: ${cssColorValue('backgroundBase')};
  color: ${cssColorValue('essentialBase')};
  font-size: 12px;
  font-weight: bold;
`;

interface FilterChipButtonProps {
  children: React.ReactNode;
  count: number;
  isOpen: boolean;
  onClick: () => void;
}

export const FilterChipButton = forwardRef<
  HTMLButtonElement,
  FilterChipButtonProps
>(({ children, count, isOpen, onClick }, ref) => {
  const ChevronIcon = isOpen ? IconChevronUp : IconChevronDown;

  return (
    <StyledButton
      $hasSelection={count > 0}
      onClick={onClick}
      ref={ref}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
    >
      {children}
      {count > 0 && <CountBadge>{count}</CountBadge>}
      <ChevronIcon iconSize={16} />
    </StyledButton>
  );
});

FilterChipButton.displayName = 'FilterChipButton';
